import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductDetail } from "../../features/buyer/buyerSlice";
import AppButton from "../../components/buyer/AppButton";
import Loader from "../../components/common/Loader";
import PlaceBidModal from "../../components/buyer/PlaceBidModal";
import theme from "../../styles/theme/theme";
import BuyConfirmModal from "../../components/buyer/BuyConfirmModal";
import "./styles.css";
import { capitalizeFirst } from "../../utils/stringUtils";
import { useParams, useNavigate } from "react-router-dom";
import defaultProductImage from "../../assets/defaultImage.png";

import {
  Alert,
  Row,
  Col,
  Container,
  Accordion,
  Tooltip,
  OverlayTrigger,
  Badge,
} from "react-bootstrap";
import {
  FaInfoCircle,
  FaQuestionCircle,
  FaMapMarkerAlt,
  FaWarehouse,
  FaTags,
  FaBoxes,
  FaUser,
  FaStickyNote,
  FaBalanceScale,
  FaShip,
  FaMoneyCheckAlt,
  FaClock,
  FaCertificate,
  FaCalendarCheck,
  FaStoreAlt,
} from "react-icons/fa";
import { m } from "framer-motion";
import Breadcrumb from "../../components/Breadcrumb";
import { generateBreadcrumbs } from "../../utils/breadcrumbs";
import KycBanner from "../../components/common/KycBanner";
import { clearProductDetail } from "../../features/seller/sellerSlice";
import BuyerChatModal from "../../components/buyer/BuyerChatModal";
import { formatCurrency, formatPrice } from "../../utils/price";
import { formatBidDateTime, timeAgo } from "../../utils/timeAgo";

const ProductDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showBidModal, setShowBidModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [countdown, setCountdown] = useState("");
  const {
    selectedProduct: product,
    productLoading,
    productError,
  } = useSelector((state) => state.buyer);
  const [showChat, setShowChat] = useState(false);
  const {
    createChatLoading,
    createChatError,
    createChatData,
    createChatSuccess,
  } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);

  const lastBid = product?.last_bid_price;
  const minAskingPrice = product?.minimum_asking_price;
  const bidIncrement = product?.bid_increment;
  const bidCloseDate = product?.bid_closed_at;
  const currency = product?.currency || "";

  // console.log('lastBid:', lastBid);
  console.log("Product:", product);

  // Map defined at component top level
  const bidInfoItems = [
    {
      label: "Last Bid",
      value: formatCurrency(lastBid),
      // lastBid != null ? `${currency} ${formatPrice(lastBid)}` : "-",
    },
    {
      label: "Minimum Asking",
      value: formatCurrency(minAskingPrice),
      // minAskingPrice != null
      //   ? `${currency} ${formatPrice(minAskingPrice)}`
      //   : "-",
    },
    {
      label: "Bid Increment",
      value: formatCurrency(bidIncrement),
      // bidIncrement != null ? `${currency} ${formatPrice(bidIncrement)}` : "-",
    },
    {
      label: "Bid Expire In",
      value: bidCloseDate ? formatBidDateTime(bidCloseDate) : "-",
    },
  ];

  // console.log("User:", user);

  useEffect(() => {
    dispatch(clearProductDetail());
    if (id) {
      dispatch(fetchProductDetail({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!bidCloseDate) return;

    const updateCountdown = () => {
      const diff = new Date(bidCloseDate) - new Date();
      if (diff <= 0) return "Bidding Closed";
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      return `${h}h ${m}m ${s}s`;
    };

    setCountdown(updateCountdown());
    const interval = setInterval(() => setCountdown(updateCountdown()), 1000);
    return () => clearInterval(interval);
  }, [bidCloseDate]);

  const handleBidSubmit = (bidData) => {
    setShowBidModal(false);
  };
  // console.log("Product:", product);
  // console.log("productId:", id);
  const breadcrumbs = generateBreadcrumbs({
    pageType: "productDetails",
    data: {
      product: {
        id: product?.id,
        name: product?.name,
      },
    },
  });

  const isBidExpired =
    product?.allow_bidding === 1 &&
    bidCloseDate &&
    new Date(bidCloseDate) <= new Date();

  if (productLoading)
    return (
      <div
        style={{
          padding: theme.spacing(5),
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );

  return (
    <div className="product-detail-page py-4">
      <Container fluid>
        {/* Filters and Search */}
        <Container maxWidth="lg" sx={{ mt: 3, mb: 1 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>
        <KycBanner />
        <h2 className="mb-3" style={{ color: theme.palette.tertiary.main }}>
          Product Details
        </h2>
        {productError ? (
          <Alert variant="danger">{productError}</Alert>
        ) : product ? (
          <>
            <Row className="gy-4">
              {/* Image row - full width */}
              <Col xs={12}>
                <div className="product-image-wrapper">
                  <img
                    // Use the landscape default image URL here
                    src={product?.image_url?.trim() || defaultProductImage}
                    alt={product.name}
                    loading="lazy"
                    // Use your custom class
                    className="product-detail-image"
                  />
                </div>
              </Col>

              {/* Product Name and Price - full width */}
              <Col xs={12}>
                <h4 className="mb-2 fw-semibold">{product.name}</h4>
                <div className="p-2 bg-light rounded mb-3 shadow-sm">
                  <h5 className="text-success fw-bold mb-0">
                    {/* A${formatPrice(product.price)} */}
                    {formatCurrency(product.price, product.currency)}
                  </h5>
                </div>
              </Col>

              {/* Bid Details Section */}

              {product.allow_bidding === 1 && (
                <>
                  {/* If bid expired -> Show message instead of bid info */}
                  {isBidExpired ? (
                    <Alert
                      variant="warning"
                      className="mt-4 text-center fw-semibold"
                      style={{
                        fontSize: "1.05rem",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FaClock style={{ fontSize: "1.2rem" }} />
                      Bidding on this product has ended.
                    </Alert>
                  ) : (
                    <>
                      {/* Header with countdown at opposite ends */}
                      <div
                        className="d-flex align-items-center justify-content-between mt-4 border-bottom pb-2"
                        style={{ fontSize: "1.1rem", fontWeight: 600 }}
                      >
                        <span>Bidding Info</span>
                        {bidCloseDate && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: 500,
                              fontSize: "0.95rem",
                              color: theme.palette.success.main,
                            }}
                            aria-live="polite"
                          >
                            Bidding closes in{" "}
                            {countdown === "Bidding Closed" ? (
                              <strong>Bidding closed</strong>
                            ) : (
                              <>
                                <FaClock aria-hidden="true" />
                                <time
                                  dateTime={bidCloseDate}
                                  style={{ fontWeight: 600 }}
                                >
                                  {countdown}
                                </time>
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Bid Info Grid */}
                      <Row className="gy-3 mt-2">
                        {bidInfoItems.map((item, idx) => (
                          <Col md={3} key={idx}>
                            <div
                              style={{
                                padding: "16px",
                                borderRadius: "12px",
                                background:
                                  theme.palette.background.default + "CC",
                                border: `1px solid ${theme.palette.divider}`,
                                textAlign: "center",
                                boxShadow: `inset 0 0 8px ${theme.palette.secondary.main}20`,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.95rem",
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {item.label}
                              </div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "1.2rem",
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {item.value}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </>
                  )}
                </>
              )}

              {/* Description Content Row */}
              <Row className="gy-4">
                {/* Left Column: badges + location + quantity */}
                <Col md={6}>
                  <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
                    <span className="badge bg-warning text-dark">
                      {capitalizeFirst(product.condition)}
                    </span>
                    <span className="badge bg-secondary">
                      {capitalizeFirst(product.payment_terms)}
                    </span>
                    <span className="badge bg-info text-dark">
                      {capitalizeFirst(product.category?.name)}
                    </span>

                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip
                          id="condition-tooltip"
                          className="custom-tooltip"
                        >
                          • Condition: Physical quality of the scrap
                          <br />
                          • Payment Terms: Agreed method or schedule of payment
                          <br />• Category: Classification of scrap material
                        </Tooltip>
                      }
                    >
                      <FaInfoCircle
                        style={{
                          color: "#F4C542",
                          cursor: "pointer",
                          marginLeft: "4px",
                          fontSize: "0.8rem",
                          verticalAlign: "super",
                        }}
                      />
                    </OverlayTrigger>
                  </div>

                  <p>
                    <FaMapMarkerAlt className="me-2" />{" "}
                    <strong>Location:</strong> {product.location}
                  </p>
                  <p>
                    <FaWarehouse className="me-2" />
                    <strong>Quantity:</strong>{" "}
                    <span
                      style={{
                        color:
                          product.stock?.quantity > 0
                            ? theme.palette.text.primary
                            : "#ff4d4f",
                        fontWeight: 600,
                      }}
                    >
                      {product.stock?.quantity > 0
                        ? `${product.stock?.quantity} ${product.unit_of_measurement}`
                        : "Out of Stock"}
                    </span>
                  </p>
                </Col>

                {/* Right Column: subcategory, incoterms, lead time */}
                <Col md={6}>
                  <p>
                    <FaTags className="me-2" />
                    <strong>Subcategory:</strong> {product.subcategory?.name}
                  </p>
                  <p>
                    <FaShip className="me-2" />
                    <strong>
                      Incoterms{" "}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            id="incoterms-tooltip"
                            className="custom-tooltip"
                          >
                            International commercial terms defining
                            responsibilities in shipping
                          </Tooltip>
                        }
                      >
                        <FaInfoCircle
                          style={{
                            color: "#F4C542",
                            cursor: "pointer",
                            marginLeft: "4px",
                            fontSize: "0.8rem",
                            verticalAlign: "super",
                          }}
                        />
                      </OverlayTrigger>{" "}
                      :
                    </strong>{" "}
                    {product.incoterms}
                  </p>

                  <p>
                    <FaClock className="me-2" />
                    <strong>
                      Lead Time{" "}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            id="leadtime-tooltip"
                            className="custom-tooltip"
                          >
                            Time taken to process and prepare product for
                            shipment
                          </Tooltip>
                        }
                      >
                        <FaInfoCircle
                          style={{
                            color: "#F4C542",
                            cursor: "pointer",
                            marginLeft: "4px",
                            fontSize: "0.8rem",
                            verticalAlign: "super",
                          }}
                        />
                      </OverlayTrigger>{" "}
                      :
                    </strong>{" "}
                    {product.lead_time} days
                  </p>
                </Col>
              </Row>
            </Row>

            <h5 className="mt-4 border-bottom pb-2">Description</h5>
            <div
              className="text-muted ql-editor"
              style={{ padding: 0 }}
              dangerouslySetInnerHTML={{ __html: product.description }}
            />

            {/* Additional Details */}
            <h5 className="mt-4 border-bottom pb-2">Additional Details</h5>
            <Row className="gy-2">
              <Col md={6}>
                {/* <p>
                  <FaBoxes className="me-2" />
                  <strong>Material Type:</strong>{" "}
                  {product.material_type || "N/A"}
                </p> */}
                <p>
                  <FaBalanceScale className="me-2" />
                  <strong>Quantity:</strong> {product.stock?.quantity}{" "}
                  {product.unit_of_measurement || "N/A"}
                </p>
                <p>
                  <FaShip className="me-2" />
                  <strong>Incoterms:</strong>{" "}
                  {product.incoterms || "Not Specified"}
                </p>
                <p>
                  <FaMoneyCheckAlt className="me-2" />
                  <strong>Payment Terms:</strong>{" "}
                  {product.payment_terms || "Not Specified"}
                </p>
                <p>
                  <FaClock className="me-2" />
                  <strong>Lead Time:</strong> {product.lead_time || "N/A"} days
                </p>
                {/* <p>
                  <FaCertificate className="me-2" />
                  <strong>Certifications:</strong>{" "}
                  {product.certifications || "None"}
                </p> */}
              </Col>

              <Col md={6}>
                <p>
                  <FaUser className="me-2" />
                  <strong>Seller:</strong> {product.seller_name || "N/A"}
                  {product.seller?.company_name && (
                    <span className="text-muted">
                      {" "}
                      — {product.seller.company_name || "N/A"}
                    </span>
                  )}
                </p>
                <p>
                  <FaMapMarkerAlt className="me-2" />
                  <strong>Seller Location:</strong>{" "}
                  {product.seller?.city ||
                  product.seller?.state ||
                  product.seller?.region ? (
                    <>
                      {product.seller?.city || "-"},{" "}
                      {product.seller?.state || "-"},{" "}
                      {product.seller?.region || "-"}
                    </>
                  ) : (
                    "N/A"
                  )}
                </p>
                <p>
                  <FaCalendarCheck className="me-2" />
                  <strong>Listed On:</strong>{" "}
                  {timeAgo(product.created_at || "N/A")}
                </p>
                <p>
                  <FaTags className="me-2" />
                  <strong>Category:</strong>{" "}
                  {product.category?.name ||
                  product.subcategory?.name ||
                  product.subcategory?.metal_code ? (
                    <>
                      {product.category?.name || "-"} /{" "}
                      {product.subcategory?.name || "-"} (
                      {product.subcategory?.metal_code || "-"})
                    </>
                  ) : (
                    "N/A"
                  )}
                </p>
                <p>
                  <FaStoreAlt className="me-2" />
                  <strong>Uploaded By:</strong> {product.seller_name || "N/A"}
                </p>

                <div className="d-flex gap-2">
                  {product.seller?.kyc_verified === 1 && (
                    <Badge bg="success">KYC Verified</Badge>
                  )}
                  {product.seller?.certification_verified === 1 && (
                    <Badge bg="info">Certified Seller</Badge>
                  )}
                  {product.seller?.is_premium === 1 && (
                    <Badge bg="warning">Premium</Badge>
                  )}
                </div>
              </Col>

              {product.notes && (
                <Col>
                  <p>
                    <FaStickyNote className="me-2" />
                    <strong>Notes:</strong> {product.notes || "N/A"}
                  </p>
                </Col>
              )}
            </Row>

            {/* FAQs */}
            {Array.isArray(product.faqs) && product.faqs.length > 0 && (
              <>
                <h5 className="mt-4 border-bottom pb-2">FAQs</h5>
                <Accordion defaultActiveKey="0">
                  {product.faqs.map((faq, index) => (
                    <Accordion.Item eventKey={index.toString()} key={index}>
                      <Accordion.Header>
                        <FaQuestionCircle className="me-2 text-warning" />
                        {faq.question}
                      </Accordion.Header>
                      <Accordion.Body>{faq.answer}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </>
            )}
          </>
        ) : (
          <p className="text-muted text-center">Product not found</p>
        )}
      </Container>

      <div
        className="floating-footer m-3 d-flex"
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        {/* Left Side: Contact Seller */}
        {/* <AppButton
          onClick={() => {
            console.log("Chat with seller clicked");
            // open Chat Modal / navigate to Chat screen
            setShowChat(true);
          }}
          variant="outlined"
          fullWidth={false}
          style={{ minWidth: "150px" }}
        >
          Contact Seller
        </AppButton> */}

        {/* Right Side: Cancel + Buy/Place Bid */}
        <div style={{ display: "flex", gap: "12px" }}>
          <AppButton
            onClick={() => navigate(-1)}
            variant="outlined"
            fullWidth={false}
            style={{ minWidth: "100px" }}
          >
            Cancel
          </AppButton>

          {product?.stock?.quantity > 0 && (
            <AppButton
              onClick={() =>
                product.allow_bidding === 1 && !isBidExpired
                  ? setShowBidModal(true)
                  : product.allow_bidding !== 1
                    ? setShowBuyModal(true)
                    : null
              }
              variant="contained"
              fullWidth={false}
              disabled={product.allow_bidding === 1 && isBidExpired}
              style={{ minWidth: "130px", opacity: isBidExpired ? 0.6 : 1 }}
            >
              {product.allow_bidding === 1 ? "Place Bid" : "Buy Now"}
            </AppButton>
          )}
        </div>
      </div>

      {/* Modals */}
      <PlaceBidModal
        show={showBidModal}
        onHide={() => setShowBidModal(false)}
        product={product}
        onSubmit={handleBidSubmit}
      />

      <BuyConfirmModal
        show={showBuyModal}
        onHide={() => setShowBuyModal(false)}
        product={product}
      />

      <BuyerChatModal
        show={showChat}
        onHide={() => setShowChat(false)}
        product={product}
        seller={product?.seller}
      />
    </div>
  );
};

export default ProductDetailPage;
